/**
 * Shop Owner Onboarding Types
 *
 * TypeScript interfaces for the 4-step shop owner onboarding wizard:
 * Step 1: Shop Setup (multiple locations)
 * Step 2: Invite Barbers (bulk invitations)
 * Step 3: Import Data (Square, Vagaro, Booksy, CSV)
 * Step 4: Review & Launch
 */

// ============================================================================
// Onboarding Status & Progress
// ============================================================================

export interface ShopOwnerOnboardingStatus {
  userId: string;
  isOnboardingComplete: boolean;
  currentStep: number;
  progress: OnboardingProgress;
  createdAt?: string;
  updatedAt?: string;
}

export interface OnboardingProgress {
  step1ShopsComplete: boolean;
  step2InvitationsComplete: boolean;
  step3ImportsComplete: boolean;
  step4ReviewComplete: boolean;
  draftData?: string; // JSON string of saved draft
}

export interface SaveProgressRequest {
  currentStep: number;
  draftData: string;
}

// ============================================================================
// Step 1: Shop Setup
// ============================================================================

export interface ShopDraft {
  tempId: string; // Client-side UUID before server save
  name: string;
  description?: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingTime: string; // Format: "HH:mm"
  closingTime: string; // Format: "HH:mm"
  daysOfWeek: number[]; // 1=Monday, 7=Sunday
  latitude?: number;
  longitude?: number;
  photos: File[];
  photoUrls?: string[]; // URLs after upload
  socialLinks: SocialLinks;
}

export interface SocialLinks {
  facebookUrl?: string;
  instagramUrl?: string;
  twitterUrl?: string;
  tiktokUrl?: string;
  youtubeUrl?: string;
  linkedinUrl?: string;
}

export interface CreatedShopDto extends Omit<ShopDraft, 'tempId' | 'photos'> {
  id: number; // Server-assigned ID
  tempId?: string; // Original client ID for mapping
  isApproved: boolean;
  isActive: boolean;
  createdAt: string;
  ownerId: string;
  ownerName?: string;
}

export interface BulkShopCreateRequest {
  shops: Omit<ShopDraft, 'tempId' | 'photos'>[];
}

export interface BulkShopCreateResponse {
  createdShops: CreatedShopDto[];
  errors: ShopCreateError[];
  totalProcessed: number;
  successCount: number;
  failureCount: number;
}

export interface ShopCreateError {
  tempId?: string;
  shopName: string;
  errorMessage: string;
  errorField?: string;
}

// ============================================================================
// Step 2: Invite Barbers
// ============================================================================

export interface BarberInvitationDraft {
  id?: string; // Client-side ID for list management
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string; // e.g., "Senior Barber", "Stylist"
  personalMessage?: string;
  targetShopId: number;
  targetShopName?: string; // For display
}

export interface CsvInvitationRow {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  shopName?: string; // Will be mapped to shopId
  personalMessage?: string;
}

export interface BulkInvitationRequest {
  invitations: Omit<BarberInvitationDraft, 'id' | 'targetShopName'>[];
}

export interface BulkInvitationResponse {
  sentInvitations: SentInvitationDto[];
  errors: InvitationError[];
  totalProcessed: number;
  successCount: number;
  failureCount: number;
}

export interface SentInvitationDto {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  targetShopId: number;
  targetShopName: string;
  status: 'Pending' | 'Sent';
  invitationToken: string;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationError {
  email: string;
  errorMessage: string;
  errorField?: string;
}

export interface SeatAvailability {
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
  pendingInvitations: number;
}

export interface ParseInvitationCsvRequest {
  csvContent: string;
}

export interface ParseInvitationCsvResponse {
  parsedRows: CsvInvitationRow[];
  errors: CsvParseError[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
}

// ============================================================================
// Step 3: Import Data
// ============================================================================

export type ImportPlatform = 'square' | 'vagaro' | 'booksy' | 'csv';
export type ImportEntityType = 'services' | 'clients' | 'appointments';

export interface PlatformInfo {
  id: ImportPlatform;
  name: string;
  description: string;
  logoUrl?: string;
  exportInstructions?: string[];
  exportInstructionsByEntity?: Partial<Record<ImportEntityType, string[]>>;
  supportsOAuth: boolean;
}

export const SUPPORTED_PLATFORMS: PlatformInfo[] = [
  {
    id: 'square',
    name: 'Square',
    description: 'Import services, clients, and appointments from Square',
    supportsOAuth: true,
    exportInstructions: [
      'Go to Square Dashboard > Items > Export',
      'Select "Export All Items" as CSV',
      'Upload the exported file below',
    ],
  },
  {
    id: 'vagaro',
    name: 'Vagaro',
    description: 'Import services, clients, and history from Vagaro',
    supportsOAuth: false,
    exportInstructions: [
      'Log in to Vagaro Pro',
      'Go to Settings > Data Export',
      'Export Services, Clients, or Appointments',
      'Upload the exported CSV file below',
    ],
  },
  {
    id: 'booksy',
    name: 'Booksy',
    description: 'Import services and client data from Booksy',
    supportsOAuth: false,
    exportInstructions: [
      'Export instructions vary by data type — select what to import below',
    ],
    exportInstructionsByEntity: {
      clients: [
        'Log in to booksy.com on a desktop browser',
        'Go to your Customer Database section',
        'Click the Export button (top-right corner)',
        'Upload the downloaded CSV file below',
      ],
      services: [
        'Booksy doesn\'t offer a direct service export',
        'Download our template below and fill in your services',
        'Include: service name, price, duration, and description',
      ],
      appointments: [
        'Contact Booksy support at info.us@booksy.com',
        'Request a CSV export of your appointment history',
        'Upload the file once received',
      ],
    },
  },
  {
    id: 'csv',
    name: 'Manual CSV',
    description: 'Upload your own CSV file with custom data',
    supportsOAuth: false,
    exportInstructions: [
      'Download our CSV template below',
      'Fill in your data following the template format',
      'Upload the completed CSV file',
    ],
  },
];

export interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
  sampleValues: string[];
  isRequired: boolean;
  isMatched: boolean;
}

export interface ImportFieldDefinition {
  field: string;
  label: string;
  required: boolean;
  type: 'string' | 'number' | 'date' | 'email' | 'phone';
  description?: string;
}

export const SERVICE_IMPORT_FIELDS: ImportFieldDefinition[] = [
  { field: 'name', label: 'Service Name', required: true, type: 'string' },
  { field: 'price', label: 'Price', required: true, type: 'number' },
  { field: 'duration', label: 'Duration (minutes)', required: true, type: 'number' },
  { field: 'description', label: 'Description', required: false, type: 'string' },
  { field: 'category', label: 'Category', required: false, type: 'string' },
];

export const CLIENT_IMPORT_FIELDS: ImportFieldDefinition[] = [
  { field: 'firstName', label: 'First Name', required: true, type: 'string' },
  { field: 'lastName', label: 'Last Name', required: true, type: 'string' },
  { field: 'email', label: 'Email', required: false, type: 'email' },
  { field: 'phone', label: 'Phone', required: false, type: 'phone' },
  { field: 'notes', label: 'Notes', required: false, type: 'string' },
];

export const APPOINTMENT_IMPORT_FIELDS: ImportFieldDefinition[] = [
  { field: 'date', label: 'Date', required: true, type: 'date' },
  { field: 'time', label: 'Time', required: true, type: 'string' },
  { field: 'clientName', label: 'Client Name', required: true, type: 'string' },
  { field: 'clientEmail', label: 'Client Email', required: false, type: 'email' },
  { field: 'serviceName', label: 'Service', required: true, type: 'string' },
  { field: 'barberName', label: 'Barber', required: false, type: 'string' },
  { field: 'status', label: 'Status', required: false, type: 'string' },
  { field: 'notes', label: 'Notes', required: false, type: 'string' },
];

export interface ParseImportCsvRequest {
  csvContent: string;
  entityType: ImportEntityType;
  platform: ImportPlatform;
}

export interface ParseImportCsvResponse {
  columns: string[];
  sampleData: Record<string, string>[];
  totalRows: number;
  suggestedMappings: ColumnMapping[];
}

export interface ExecuteImportRequest {
  entityType: ImportEntityType;
  platform: ImportPlatform;
  csvContent: string;
  columnMappings: ColumnMapping[];
  targetShopId: number;
}

export interface ImportProgress {
  importId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  entityType: ImportEntityType;
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  failureCount: number;
  startedAt?: string;
  completedAt?: string;
}

export interface ImportResult {
  importId: string;
  entityType: ImportEntityType;
  platform: ImportPlatform;
  totalRecords: number;
  successCount: number;
  failureCount: number;
  skippedCount: number;
  errors: ImportError[];
  createdEntities: ImportedEntity[];
}

export interface ImportError {
  rowNumber: number;
  field?: string;
  message: string;
  rawValue?: string;
}

export interface ImportedEntity {
  id: number;
  name: string;
  entityType: ImportEntityType;
}

export interface CsvParseError {
  rowNumber: number;
  column?: string;
  message: string;
  rawValue?: string;
}

// ============================================================================
// Step 4: Review & Launch
// ============================================================================

export interface OnboardingSummary {
  shops: CreatedShopDto[];
  invitations: {
    sent: SentInvitationDto[];
    pending: BarberInvitationDraft[];
    totalCount: number;
  };
  imports: {
    services: ImportSummary;
    clients: ImportSummary;
    appointments: ImportSummary;
  };
  subscription?: SubscriptionSummary;
}

export interface ImportSummary {
  imported: number;
  failed: number;
  skipped: number;
}

export interface SubscriptionSummary {
  planName: string;
  status: string;
  barberSeats: number;
  usedSeats: number;
}

export interface CompleteOnboardingRequest {
  skipRemaining?: boolean;
}

export interface CompleteOnboardingResponse {
  success: boolean;
  message: string;
  redirectUrl: string;
}

// ============================================================================
// Wizard State
// ============================================================================

export interface ShopOwnerOnboardingState {
  // Overall state
  currentStep: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Step 1: Shops
  shops: ShopDraft[];
  createdShops: CreatedShopDto[];
  editingShopIndex: number | null;

  // Step 2: Invitations
  pendingInvitations: BarberInvitationDraft[];
  sentInvitations: SentInvitationDto[];
  seatAvailability: SeatAvailability | null;

  // Step 3: Imports
  selectedPlatform: ImportPlatform | null;
  selectedEntityType: ImportEntityType | null;
  csvData: string | null;
  parsedData: ParseImportCsvResponse | null;
  columnMappings: ColumnMapping[];
  importProgress: ImportProgress | null;
  importResults: ImportResult[];

  // Step 4: Summary
  summary: OnboardingSummary | null;
}

// ============================================================================
// Component Props
// ============================================================================

export interface StepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  isLoading?: boolean;
}

export interface Step1Props extends StepProps {
  shops: ShopDraft[];
  createdShops: CreatedShopDto[];
  onAddShop: (shop: ShopDraft) => void;
  onEditShop: (index: number, shop: ShopDraft) => void;
  onRemoveShop: (index: number) => void;
  onSaveShops: () => Promise<void>;
}

export interface Step2Props extends StepProps {
  createdShops: CreatedShopDto[];
  pendingInvitations: BarberInvitationDraft[];
  sentInvitations: SentInvitationDto[];
  seatAvailability: SeatAvailability | null;
  onAddInvitation: (invitation: BarberInvitationDraft) => void;
  onRemoveInvitation: (index: number) => void;
  onImportCsv: (csvContent: string) => Promise<void>;
  onSendInvitations: () => Promise<void>;
}

export interface Step3Props extends StepProps {
  createdShops: CreatedShopDto[];
  selectedPlatform: ImportPlatform | null;
  selectedEntityType: ImportEntityType | null;
  parsedData: ParseImportCsvResponse | null;
  columnMappings: ColumnMapping[];
  importProgress: ImportProgress | null;
  importResults: ImportResult[];
  onSelectPlatform: (platform: ImportPlatform) => void;
  onSelectEntityType: (type: ImportEntityType) => void;
  onUploadCsv: (csvContent: string) => Promise<void>;
  onUpdateMappings: (mappings: ColumnMapping[]) => void;
  onExecuteImport: (shopId: number) => Promise<void>;
  onResetImport: () => void;
}

export interface Step4Props extends StepProps {
  summary: OnboardingSummary | null;
  onComplete: () => Promise<void>;
  onEditStep: (step: number) => void;
}
